// TelcoTrack — Warehouse API (AutoCRM Modul Sklad)
// Gateway: `/api/objects/warehouse/*`. Envelope `{ success, data }`.

import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, from, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import type {
  GoodsIssue,
  GoodsReceipt,
  InventoryCheckPreview,
  InventoryCheckResult,
  ObjectMaterialIssuePreview,
  SapStockAvailability,
  WarehouseEntity,
  WarehouseGoodsReceiptAttachment,
  WarehouseGoodsReceiptDocument,
  WarehouseInventoryCheck,
  WarehouseItem,
  WarehouseListResult,
  WarehousePosition,
  WarehouseStatusInfo,
  WarehouseStockSnapshot,
  WarehouseScanResolveResult,
  WarehouseTransfer,
  WarehouseTransferPreview,
} from './warehouse.types';
import type { MaterialWarehouseMinStockDto, WarehouseMovementDto } from '@core/shared-bridge';
import type {
  WarehouseProfileDto,
  WarehouseProfileUpdateInput,
  WarehouseRegistryCreateInput,
} from '@core/shared-bridge';

export type {
  GoodsIssue,
  GoodsIssueStatus,
  GoodsReceipt,
  WarehouseGoodsReceiptAttachment,
  WarehouseGoodsReceiptDocument,
  InventoryCheckPreview,
  InventoryCheckResult,
  InventoryDifferenceLine,
  InventorySoupisLine,
  ObjectMaterialIssuePreview,
  PhysicalInventory,
  WarehouseInventoryCheck,
  WarehouseInventoryCheckItem,
  SapStockAvailability,
  WarehouseEntity,
  WarehouseItem,
  WarehouseListResult,
  WarehousePosition,
  WarehouseStatusInfo,
  WarehouseStockSnapshot,
  WarehouseScanResolveResult,
  WarehouseTransfer,
  WarehouseTransferLineItem,
  WarehouseTransferPreview,
  WarehouseTransferStatus,
} from './warehouse.types';

export {
  GOODS_ISSUE_STATUS_FLOW,
  WAREHOUSE_TRANSFER_STATUS_FLOW,
} from './warehouse.types';

/** Reporting: hodnota zásob po skladech. */
export interface WarehouseStockValueReportRow {
  warehouseId: string;
  warehouseName: string | null;
  itemCount: number;
  quantityTotal: number;
  valueTotal: number;
  pricedItemCount: number;
}

export interface WarehouseStockValueReport {
  warehouses: WarehouseStockValueReportRow[];
  totals: {
    itemCount: number;
    quantityTotal: number;
    valueTotal: number;
    pricedItemCount: number;
  };
}

/** ARES autofill (GET …/warehouse/ares/:ico). */
export interface WarehouseAresLookup {
  ico: string;
  name: string | null;
  dic: string | null;
  legalForm: string | null;
  address: {
    text: string | null;
    street: string | null;
    city: string | null;
    zip: string | null;
    country: string;
  };
}

export interface WarehouseDnOcrSuggestedLine {
  rawText: string;
  rawName: string | null;
  rawSku: string | null;
  rawMaterialId: string | null;
  quantity: number | null;
  unitCost: number | null;
  matched: boolean;
  sapCode: string | null;
  productName: string | null;
  productUnit: string | null;
  matchScore: number | null;
  note: string | null;
}

export interface WarehouseDnOcrPreview {
  extractedText: string | null;
  ocrProvider: string;
  ocrMethod: string;
  ocrError: string | null;
  partnerName: string | null;
  partnerIco: string | null;
  documentNumber: string | null;
  referenceIdentifier: string | null;
  issueDate: string | null;
  suggestedLines: WarehouseDnOcrSuggestedLine[];
  unmatchedNotes: string | null;
  receiptDocumentId?: string | null;
  received?: boolean;
}

interface ApiEnvelope<T> {
  success?: boolean;
  data?: T;
  message?: string;
}

@Injectable({ providedIn: 'root' })
export class WarehouseService {
  private readonly apiUrl = `${environment.apiUrl}${environment.objectServiceUrl}/warehouse`;

  constructor(private readonly http: HttpClient) {}

  getStatus(): Observable<WarehouseStatusInfo> {
    return this.http
      .get<ApiEnvelope<WarehouseStatusInfo>>(`${this.apiUrl}/status`)
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  getStock(warehouseId?: string): Observable<WarehouseStockSnapshot> {
    let params = new HttpParams();
    if (warehouseId?.trim()) {
      params = params.set('warehouseId', warehouseId.trim());
    }
    return this.http
      .get<ApiEnvelope<WarehouseStockSnapshot>>(`${this.apiUrl}/stock`, { params })
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  /** Označení fyzického kusu (smotek) — PATCH metadata, ne množství. */
  updateStockItemLabel(id: string, label: string | null): Observable<WarehouseItem> {
    return this.http
      .patch<ApiEnvelope<WarehouseItem>>(
        `${this.apiUrl}/stock/items/${encodeURIComponent(id)}`,
        { label },
      )
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  /** Šarže kusu — PATCH metadata i po naskladnění (role sklad). */
  updateStockItemBatch(id: string, batchNumber: string | null): Observable<WarehouseItem> {
    return this.http
      .patch<ApiEnvelope<WarehouseItem>>(
        `${this.apiUrl}/stock/items/${encodeURIComponent(id)}`,
        { batchNumber },
      )
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  /** Jeden fyzický kus skladu (karta kusu). */
  getStockItem(id: string): Observable<{ item: WarehouseItem; warehouseName: string | null }> {
    return this.http
      .get<ApiEnvelope<{ item: WarehouseItem; warehouseName: string | null }>>(
        `${this.apiUrl}/stock/items/${encodeURIComponent(id)}`,
      )
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  /** Zásoby jednoho SAP napříč sklady (detail materiálu — bez N+1). */
  getStockBySap(sapCode: string): Observable<{
    sapCode: string;
    warehouses: Array<{
      warehouseId: string;
      warehouseName: string | null;
      items: WarehouseItem[];
    }>;
  }> {
    const params = new HttpParams().set('sapCode', sapCode.trim());
    return this.http
      .get<
        ApiEnvelope<{
          sapCode: string;
          warehouses: Array<{
            warehouseId: string;
            warehouseName: string | null;
            items: WarehouseItem[];
          }>;
        }>
      >(`${this.apiUrl}/stock`, { params })
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  /** Reporting: hodnota zásob po skladech (qty × unitPrice). */
  getStockValueReport(warehouseId?: string): Observable<WarehouseStockValueReport> {
    let params = new HttpParams();
    if (warehouseId?.trim()) {
      params = params.set('warehouseId', warehouseId.trim());
    }
    return this.http
      .get<ApiEnvelope<WarehouseStockValueReport>>(`${this.apiUrl}/reports/stock-value`, {
        params,
      })
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  /** Historie pohybů — SAP (typ) nebo warehouseItemId (konkrétní kus). */
  listMovements(opts: {
    sapCode?: string;
    warehouseItemId?: string;
    warehouseId?: string;
    limit?: number;
  }): Observable<{ list: WarehouseMovementDto[] }> {
    let params = new HttpParams();
    if (opts.sapCode?.trim()) {
      params = params.set('sapCode', opts.sapCode.trim());
    }
    if (opts.warehouseItemId?.trim()) {
      params = params.set('warehouseItemId', opts.warehouseItemId.trim());
    }
    if (opts.warehouseId?.trim()) {
      params = params.set('warehouseId', opts.warehouseId.trim());
    }
    if (opts.limit != null && Number.isFinite(opts.limit)) {
      params = params.set('limit', String(opts.limit));
    }
    return this.http
      .get<ApiEnvelope<{ list: WarehouseMovementDto[] }>>(`${this.apiUrl}/movements`, { params })
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  listWarehouses(
    maxSize?: number,
    opts?: { scope?: 'stock' | 'transfer' },
  ): Observable<WarehouseListResult> {
    let params = new HttpParams();
    if (maxSize != null && Number.isFinite(maxSize)) {
      params = params.set('maxSize', String(maxSize));
    }
    if (opts?.scope === 'transfer') {
      params = params.set('scope', 'transfer');
    }
    return this.http
      .get<ApiEnvelope<WarehouseListResult>>(`${this.apiUrl}/warehouses`, { params })
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  createWarehouse(body: {
    name: string;
    type?: 'Simple' | 'Positional';
    description?: string;
    isVirtual?: boolean;
  }): Observable<WarehouseEntity> {
    return this.http
      .post<ApiEnvelope<WarehouseEntity>>(`${this.apiUrl}/warehouses`, body)
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  deleteWarehouse(id: string): Observable<{ id: string }> {
    return this.http
      .delete<ApiEnvelope<{ id: string }>>(
        `${this.apiUrl}/warehouses/${encodeURIComponent(id)}`,
      )
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  // --- TT registry (Materiály › Sklady): warehouse_profile ---

  listRegistryWarehouses(): Observable<{ list: WarehouseProfileDto[] }> {
    return this.http
      .get<ApiEnvelope<{ list: WarehouseProfileDto[] }>>(`${this.apiUrl}/registry`)
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  getRegistryWarehouse(warehouseId: string): Observable<WarehouseProfileDto> {
    return this.http
      .get<ApiEnvelope<WarehouseProfileDto>>(
        `${this.apiUrl}/registry/${encodeURIComponent(warehouseId)}`,
      )
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  createRegistryWarehouse(body: WarehouseRegistryCreateInput): Observable<WarehouseProfileDto> {
    return this.http
      .post<ApiEnvelope<WarehouseProfileDto>>(`${this.apiUrl}/registry`, body)
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  updateRegistryWarehouse(
    warehouseId: string,
    body: WarehouseProfileUpdateInput,
  ): Observable<WarehouseProfileDto> {
    return this.http
      .patch<ApiEnvelope<WarehouseProfileDto>>(
        `${this.apiUrl}/registry/${encodeURIComponent(warehouseId)}`,
        body,
      )
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  deleteRegistryWarehouse(warehouseId: string): Observable<{ id: string }> {
    return this.http
      .delete<ApiEnvelope<{ id: string }>>(
        `${this.apiUrl}/registry/${encodeURIComponent(warehouseId)}`,
      )
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  addStock(body: {
    warehouseId?: string;
    description?: string;
    autoReceive?: boolean;
    supplierId?: string;
    supplierName?: string;
    items: {
      sapCode: string;
      quantity: number;
      positionId?: string;
      unitPrice?: number;
      supplierName?: string;
      batchNumber?: string | null;
    }[];
  }): Observable<{
    receipt: GoodsReceipt;
    received: boolean;
    mode?: 'goods_receipt' | 'warehouse_item_fallback';
    document?: WarehouseGoodsReceiptDocument;
  }> {
    return this.http
      .post<
        ApiEnvelope<{
          receipt: GoodsReceipt;
          received: boolean;
          mode?: 'goods_receipt' | 'warehouse_item_fallback';
          document?: WarehouseGoodsReceiptDocument;
        }>
      >(`${this.apiUrl}/stock/add`, body)
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  listGoodsReceipts(opts?: {
    warehouseId?: string;
    dateFrom?: string;
    dateTo?: string;
    minDate?: string;
    maxDate?: string;
    limit?: number;
  }): Observable<{ list: WarehouseGoodsReceiptDocument[] }> {
    let params = new HttpParams();
    if (opts?.warehouseId?.trim()) params = params.set('warehouseId', opts.warehouseId.trim());
    if (opts?.dateFrom?.trim()) params = params.set('dateFrom', opts.dateFrom.trim());
    if (opts?.dateTo?.trim()) params = params.set('dateTo', opts.dateTo.trim());
    if (opts?.minDate?.trim()) params = params.set('minDate', opts.minDate.trim());
    if (opts?.maxDate?.trim()) params = params.set('maxDate', opts.maxDate.trim());
    if (opts?.limit != null && Number.isFinite(opts.limit)) {
      params = params.set('limit', String(opts.limit));
    }
    return this.http
      .get<ApiEnvelope<{ list: WarehouseGoodsReceiptDocument[] }>>(
        `${this.apiUrl}/goods-receipt`,
        { params },
      )
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  /** Součet příjemek v období — bez stropu seznamu. */
  summarizeGoodsReceipts(opts?: {
    warehouseId?: string;
    dateFrom?: string;
    dateTo?: string;
    minDate?: string;
    maxDate?: string;
  }): Observable<{ totalPrice: number; documentCount: number }> {
    let params = new HttpParams();
    if (opts?.warehouseId?.trim()) params = params.set('warehouseId', opts.warehouseId.trim());
    if (opts?.dateFrom?.trim()) params = params.set('dateFrom', opts.dateFrom.trim());
    if (opts?.dateTo?.trim()) params = params.set('dateTo', opts.dateTo.trim());
    if (opts?.minDate?.trim()) params = params.set('minDate', opts.minDate.trim());
    if (opts?.maxDate?.trim()) params = params.set('maxDate', opts.maxDate.trim());
    return this.http
      .get<ApiEnvelope<{ totalPrice: number; documentCount: number }>>(
        `${this.apiUrl}/goods-receipt/summary`,
        { params },
      )
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  listGoodsIssues(opts?: {
    warehouseId?: string;
    status?: string;
    createdBy?: string;
    /** CSV id polygonů. */
    polygonId?: string;
    dateFrom?: string;
    dateTo?: string;
    minDate?: string;
    maxDate?: string;
    limit?: number;
  }): Observable<{ list: GoodsIssue[] }> {
    let params = new HttpParams();
    if (opts?.warehouseId?.trim()) params = params.set('warehouseId', opts.warehouseId.trim());
    if (opts?.status?.trim()) params = params.set('status', opts.status.trim());
    if (opts?.createdBy?.trim()) params = params.set('createdBy', opts.createdBy.trim());
    if (opts?.polygonId?.trim()) params = params.set('polygonId', opts.polygonId.trim());
    if (opts?.dateFrom?.trim()) params = params.set('dateFrom', opts.dateFrom.trim());
    if (opts?.dateTo?.trim()) params = params.set('dateTo', opts.dateTo.trim());
    if (opts?.minDate?.trim()) params = params.set('minDate', opts.minDate.trim());
    if (opts?.maxDate?.trim()) params = params.set('maxDate', opts.maxDate.trim());
    if (opts?.limit != null && Number.isFinite(opts.limit)) {
      params = params.set('limit', String(opts.limit));
    }
    return this.http
      .get<ApiEnvelope<{ list: GoodsIssue[] }>>(`${this.apiUrl}/goods-issue`, { params })
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  /** Součet výdejek v období — bez stropu seznamu. */
  summarizeGoodsIssues(opts?: {
    warehouseId?: string;
    status?: string;
    dateFrom?: string;
    dateTo?: string;
    minDate?: string;
    maxDate?: string;
  }): Observable<{ totalPrice: number; documentCount: number }> {
    let params = new HttpParams();
    if (opts?.warehouseId?.trim()) params = params.set('warehouseId', opts.warehouseId.trim());
    if (opts?.status?.trim()) params = params.set('status', opts.status.trim());
    if (opts?.dateFrom?.trim()) params = params.set('dateFrom', opts.dateFrom.trim());
    if (opts?.dateTo?.trim()) params = params.set('dateTo', opts.dateTo.trim());
    if (opts?.minDate?.trim()) params = params.set('minDate', opts.minDate.trim());
    if (opts?.maxDate?.trim()) params = params.set('maxDate', opts.maxDate.trim());
    return this.http
      .get<ApiEnvelope<{ totalPrice: number; documentCount: number }>>(
        `${this.apiUrl}/goods-issue/summary`,
        { params },
      )
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  removeStock(body: {
    warehouseId?: string;
    description?: string;
    requireSufficient?: boolean;
    autoIssue?: boolean;
    fromName?: string;
    fromIco?: string;
    fromAddress?: string;
    toName?: string;
    toIco?: string;
    toAddress?: string;
    items: { sapCode: string; quantity: number; warehouseItemId?: string }[];
  }): Observable<{ goodsIssue: GoodsIssue; issued: boolean }> {
    return this.http
      .post<ApiEnvelope<{ goodsIssue: GoodsIssue; issued: boolean }>>(
        `${this.apiUrl}/stock/remove`,
        body,
      )
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  resolveScan(body: {
    code?: string;
    warehouseId?: string;
    sapCode?: string;
    warehouseItemId?: string;
  }): Observable<WarehouseScanResolveResult> {
    return this.http
      .post<ApiEnvelope<WarehouseScanResolveResult>>(`${this.apiUrl}/scan/resolve`, body)
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  takeFromScan(body: {
    code?: string;
    warehouseId?: string;
    sapCode?: string;
    warehouseItemId?: string;
    quantity: number;
    description?: string;
    autoIssue?: boolean;
  }): Observable<{
    goodsIssue: GoodsIssue;
    issued: boolean;
    resolve: WarehouseScanResolveResult;
  }> {
    return this.http
      .post<
        ApiEnvelope<{ goodsIssue: GoodsIssue; issued: boolean; resolve: WarehouseScanResolveResult }>
      >(`${this.apiUrl}/scan/take`, body)
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  getAvailability(params: {
    sap?: string[];
    objectId?: string;
    warehouseId?: string;
  }): Observable<{ warehouseId: string; items: SapStockAvailability[] }> {
    let httpParams = new HttpParams();
    if (params.sap?.length) {
      httpParams = httpParams.set('sap', params.sap.map((s) => s.trim()).filter(Boolean).join(','));
    }
    if (params.objectId?.trim()) {
      httpParams = httpParams.set('objectId', params.objectId.trim());
    }
    if (params.warehouseId?.trim()) {
      httpParams = httpParams.set('warehouseId', params.warehouseId.trim());
    }
    return this.http
      .get<ApiEnvelope<{ warehouseId: string; items: SapStockAvailability[] }>>(
        `${this.apiUrl}/availability`,
        { params: httpParams },
      )
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  previewGoodsIssue(objectId: string, warehouseId?: string): Observable<ObjectMaterialIssuePreview> {
    const body: { objectId: string; warehouseId?: string } = { objectId };
    if (warehouseId?.trim()) body.warehouseId = warehouseId.trim();
    return this.http
      .post<ApiEnvelope<ObjectMaterialIssuePreview>>(`${this.apiUrl}/goods-issue/preview`, body)
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  createGoodsIssue(body: {
    objectId: string;
    warehouseId?: string;
    requireSufficient?: boolean;
    description?: string;
  }): Observable<{ preview: ObjectMaterialIssuePreview; goodsIssue: GoodsIssue }> {
    return this.http
      .post<ApiEnvelope<{ preview: ObjectMaterialIssuePreview; goodsIssue: GoodsIssue }>>(
        `${this.apiUrl}/goods-issue`,
        body,
      )
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  advanceGoodsIssue(id: string): Observable<GoodsIssue> {
    return this.http
      .post<ApiEnvelope<GoodsIssue>>(
        `${this.apiUrl}/goods-issue/${encodeURIComponent(id)}/advance`,
        {},
      )
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  issueGoodsIssue(id: string): Observable<GoodsIssue> {
    return this.http
      .post<ApiEnvelope<GoodsIssue>>(
        `${this.apiUrl}/goods-issue/${encodeURIComponent(id)}/issue`,
        {},
      )
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  retryAutoGoodsIssueForMaterialUsage(materialUsageId: string): Observable<{
    skipped: boolean;
    reason?: string;
    materialUsageId: string;
    goodsIssueId?: string;
    status?: string;
    issued?: boolean;
  }> {
    return this.http
      .post<
        ApiEnvelope<{
          skipped: boolean;
          reason?: string;
          materialUsageId: string;
          goodsIssueId?: string;
          status?: string;
          issued?: boolean;
        }>
      >(
        `${this.apiUrl}/material-usage/${encodeURIComponent(materialUsageId)}/retry-auto-issue`,
        {},
      )
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  listTransfers(params?: {
    /** Jedno ID nebo CSV více ID (Odkud). */
    warehouseFromId?: string;
    /** Jedno ID nebo CSV více ID (Kam). */
    warehouseToId?: string;
    dateFrom?: string;
    dateTo?: string;
    minDate?: string;
    maxDate?: string;
  }): Observable<{ list: WarehouseTransfer[] }> {
    let httpParams = new HttpParams();
    if (params?.warehouseFromId?.trim()) {
      httpParams = httpParams.set('warehouseFromId', params.warehouseFromId.trim());
    }
    if (params?.warehouseToId?.trim()) {
      httpParams = httpParams.set('warehouseToId', params.warehouseToId.trim());
    }
    if (params?.dateFrom?.trim()) httpParams = httpParams.set('dateFrom', params.dateFrom.trim());
    if (params?.dateTo?.trim()) httpParams = httpParams.set('dateTo', params.dateTo.trim());
    if (params?.minDate?.trim()) httpParams = httpParams.set('minDate', params.minDate.trim());
    if (params?.maxDate?.trim()) httpParams = httpParams.set('maxDate', params.maxDate.trim());
    return this.http
      .get<ApiEnvelope<{ list: WarehouseTransfer[] }>>(`${this.apiUrl}/transfers`, {
        params: httpParams,
      })
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  previewTransfer(body: {
    warehouseToId: string;
    warehouseFromId?: string;
    items: { sapCode: string; quantity: number }[];
  }): Observable<WarehouseTransferPreview> {
    return this.http
      .post<ApiEnvelope<WarehouseTransferPreview>>(`${this.apiUrl}/transfer/preview`, body)
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  createTransfer(body: {
    warehouseToId: string;
    warehouseFromId?: string;
    items?: { sapCode: string; quantity: number; warehouseItemId?: string }[];
    name?: string;
    description?: string;
    requireSufficient?: boolean;
  }): Observable<{
    preview: WarehouseTransferPreview;
    transfer: WarehouseTransfer;
    items?: WarehouseTransfer['items'];
  }> {
    return this.http
      .post<
        ApiEnvelope<{
          preview: WarehouseTransferPreview;
          transfer: WarehouseTransfer;
          items?: WarehouseTransfer['items'];
        }>
      >(`${this.apiUrl}/transfer`, body)
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  getTransfer(id: string): Observable<WarehouseTransfer> {
    return this.http
      .get<ApiEnvelope<WarehouseTransfer>>(
        `${this.apiUrl}/transfer/${encodeURIComponent(id)}`,
      )
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  /** @deprecated UI používá createTransfer (= execute). Ponecháno pro legacy API. */
  advanceTransfer(id: string): Observable<WarehouseTransfer> {
    return this.http
      .post<ApiEnvelope<WarehouseTransfer>>(
        `${this.apiUrl}/transfer/${encodeURIComponent(id)}/advance`,
        {},
      )
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  /** @deprecated UI používá createTransfer (= execute). Ponecháno pro legacy API. */
  commitTransfer(id: string): Observable<WarehouseTransfer> {
    return this.http
      .post<ApiEnvelope<WarehouseTransfer>>(
        `${this.apiUrl}/transfer/${encodeURIComponent(id)}/commit`,
        {},
      )
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  listPendingTransferConfirmations(): Observable<{ list: WarehouseTransfer[] }> {
    return this.http
      .get<ApiEnvelope<{ list: WarehouseTransfer[] }>>(
        `${this.apiUrl}/transfers/pending-confirmation`,
      )
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  confirmTransfer(id: string): Observable<WarehouseTransfer> {
    return this.http
      .post<ApiEnvelope<WarehouseTransfer>>(
        `${this.apiUrl}/transfer/${encodeURIComponent(id)}/confirm`,
        {},
      )
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  rejectTransfer(id: string): Observable<WarehouseTransfer> {
    return this.http
      .post<ApiEnvelope<WarehouseTransfer>>(
        `${this.apiUrl}/transfer/${encodeURIComponent(id)}/reject`,
        {},
      )
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  getPositions(
    warehouseId: string,
    parentId?: string,
  ): Observable<{ warehouseId: string; positions: WarehousePosition[] }> {
    let params = new HttpParams().set('warehouseId', warehouseId);
    if (parentId !== undefined) {
      params = params.set('parentId', parentId);
    }
    return this.http
      .get<ApiEnvelope<{ warehouseId: string; positions: WarehousePosition[] }>>(
        `${this.apiUrl}/positions`,
        { params },
      )
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  /** Soupis zásob pro inventuru (neosobní i osobní) — GET /inventory-check/preview. */
  previewInventoryCheck(warehouseId: string): Observable<InventoryCheckPreview> {
    const params = new HttpParams().set('warehouseId', warehouseId.trim());
    return this.http
      .get<ApiEnvelope<InventoryCheckPreview>>(`${this.apiUrl}/inventory-check/preview`, {
        params,
      })
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  /**
   * Zápis PhysicalInventory + dopočet rozdílů.
   * `applyCorrections` = korekce zásob (přebytek add / manko remove).
   */
  submitInventoryCheck(body: {
    warehouseId: string;
    name?: string;
    description?: string;
    applyCorrections?: boolean;
    items: {
      sapCode?: string;
      productId?: string;
      quantity: number;
      positionId?: string;
      warehouseItemId?: string | null;
      batchNumber?: string | null;
      systemQuantity?: number;
    }[];
  }): Observable<InventoryCheckResult> {
    return this.http
      .post<ApiEnvelope<InventoryCheckResult>>(`${this.apiUrl}/inventory-check`, body)
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  listInventoryChecks(opts?: {
    warehouseId?: string;
    dateFrom?: string;
    dateTo?: string;
    q?: string;
    limit?: number;
  }): Observable<{ list: WarehouseInventoryCheck[] }> {
    let params = new HttpParams();
    if (opts?.warehouseId?.trim()) params = params.set('warehouseId', opts.warehouseId.trim());
    if (opts?.dateFrom?.trim()) params = params.set('dateFrom', opts.dateFrom.trim());
    if (opts?.dateTo?.trim()) params = params.set('dateTo', opts.dateTo.trim());
    if (opts?.q?.trim()) params = params.set('q', opts.q.trim());
    if (opts?.limit != null && Number.isFinite(opts.limit)) {
      params = params.set('limit', String(opts.limit));
    }
    return this.http
      .get<ApiEnvelope<{ list: WarehouseInventoryCheck[] }>>(`${this.apiUrl}/inventory-checks`, {
        params,
      })
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  getInventoryCheck(id: string): Observable<WarehouseInventoryCheck> {
    return this.http
      .get<ApiEnvelope<WarehouseInventoryCheck>>(`${this.apiUrl}/inventory-checks/${encodeURIComponent(id)}`)
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  createInventoryCheckDraft(body: {
    warehouseId: string;
    name?: string;
    description?: string;
    seedFromStock?: boolean;
    positionIds?: string[];
  }): Observable<WarehouseInventoryCheck> {
    return this.http
      .post<ApiEnvelope<WarehouseInventoryCheck>>(`${this.apiUrl}/inventory-checks`, body)
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  updateInventoryCheckDraft(
    id: string,
    body: {
      name?: string;
      description?: string;
      items: Array<{
        sapCode: string;
        productId?: string | null;
        productName?: string | null;
        unit?: string | null;
        systemQuantity?: number;
        countedQuantity: number;
        positionId?: string | null;
        warehouseItemId?: string | null;
        label?: string | null;
        batchNumber?: string | null;
      }>;
    },
  ): Observable<WarehouseInventoryCheck> {
    return this.http
      .put<ApiEnvelope<WarehouseInventoryCheck>>(
        `${this.apiUrl}/inventory-checks/${encodeURIComponent(id)}`,
        body,
      )
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  confirmInventoryCheckDraft(
    id: string,
    body?: { applyCorrections?: boolean },
  ): Observable<InventoryCheckResult> {
    return this.http
      .post<ApiEnvelope<InventoryCheckResult>>(
        `${this.apiUrl}/inventory-checks/${encodeURIComponent(id)}/confirm`,
        body ?? {},
      )
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  cancelInventoryCheckDraft(id: string): Observable<WarehouseInventoryCheck> {
    return this.http
      .post<ApiEnvelope<WarehouseInventoryCheck>>(
        `${this.apiUrl}/inventory-checks/${encodeURIComponent(id)}/cancel`,
        {},
      )
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  deleteInventoryCheckDraft(id: string): Observable<{ deleted: boolean }> {
    return this.http
      .delete<ApiEnvelope<{ deleted: boolean }>>(
        `${this.apiUrl}/inventory-checks/${encodeURIComponent(id)}`,
      )
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  /** Minimální zásoby (TelcoTrack DB) — filtr sapCode a/nebo warehouseId(s). */
  listMinStock(opts: {
    sapCode?: string;
    warehouseId?: string;
    warehouseIds?: string[];
  }): Observable<MaterialWarehouseMinStockDto[]> {
    let params = new HttpParams();
    if (opts.sapCode?.trim()) params = params.set('sapCode', opts.sapCode.trim());
    if (opts.warehouseId?.trim()) {
      params = params.set('warehouseId', opts.warehouseId.trim());
    }
    if (opts.warehouseIds?.length) {
      params = params.set(
        'warehouseIds',
        opts.warehouseIds.map((id) => id.trim()).filter(Boolean).join(','),
      );
    }
    return this.http
      .get<ApiEnvelope<MaterialWarehouseMinStockDto[]>>(`${this.apiUrl}/min-stock`, { params })
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  /**
   * Nastaví minimum ve skladové (usage) jednotce. `minQuantity` null = smazat.
   * ACL: sklad/manager/admin (stejně jako katalog).
   */
  upsertMinStock(body: {
    sapCode: string;
    warehouseId: string;
    minQuantity: number | null;
  }): Observable<MaterialWarehouseMinStockDto | null> {
    return this.http
      .put<ApiEnvelope<MaterialWarehouseMinStockDto | null>>(`${this.apiUrl}/min-stock`, body)
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  clearMinStock(sapCode: string, warehouseId: string): Observable<{ removed: boolean }> {
    const params = new HttpParams()
      .set('sapCode', sapCode.trim())
      .set('warehouseId', warehouseId.trim());
    return this.http
      .delete<ApiEnvelope<{ removed: boolean }>>(`${this.apiUrl}/min-stock`, { params })
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  /** ARES IČO → jméno/adresa (vyžaduje ARES_ENABLED=1). */
  lookupAres(ico: string): Observable<WarehouseAresLookup> {
    return this.http
      .get<ApiEnvelope<WarehouseAresLookup>>(
        `${this.apiUrl}/ares/${encodeURIComponent(ico.trim())}`,
      )
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  /** OCR upload dodacího listu → náhled (multipart `file`). */
  ocrDeliveryNoteFromDocument(
    file: File,
    opts?: {
      warehouseId?: string;
      createDraft?: boolean;
      autoReceive?: boolean;
      partnerName?: string;
      partnerIco?: string;
      documentNumber?: string;
      notes?: string;
    },
  ): Observable<WarehouseDnOcrPreview> {
    const form = new FormData();
    form.append('file', file, file.name);
    if (opts?.warehouseId?.trim()) form.append('warehouseId', opts.warehouseId.trim());
    if (opts?.createDraft) form.append('createDraft', 'true');
    if (opts?.autoReceive === false) form.append('autoReceive', 'false');
    if (opts?.partnerName?.trim()) form.append('partner_name', opts.partnerName.trim());
    if (opts?.partnerIco?.trim()) form.append('partner_ico', opts.partnerIco.trim());
    if (opts?.documentNumber?.trim()) form.append('document_number', opts.documentNumber.trim());
    if (opts?.notes?.trim()) form.append('notes', opts.notes.trim());
    return this.http
      .post<ApiEnvelope<WarehouseDnOcrPreview>>(
        `${this.apiUrl}/delivery-notes/from-document`,
        form,
      )
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  /** Potvrzení OCR náhledu → příjemka (multipart: volitelný zdrojový DL). */
  confirmDeliveryNote(
    body: {
      warehouseId: string;
      partnerName?: string;
      partnerIco?: string;
      documentNumber?: string;
      notes?: string;
      issueDate?: string;
      autoReceive?: boolean;
      lines: Array<{
        sapCode?: string;
        quantity?: number;
        unitCost?: number;
        rawMaterialId?: string;
      }>;
    },
    sourceFile?: File | null,
  ): Observable<{ documentId: string; received: boolean; receiptId: string }> {
    const form = new FormData();
    form.append('warehouseId', body.warehouseId);
    if (body.partnerName?.trim()) form.append('partnerName', body.partnerName.trim());
    if (body.partnerIco?.trim()) form.append('partnerIco', body.partnerIco.trim());
    if (body.documentNumber?.trim()) form.append('documentNumber', body.documentNumber.trim());
    if (body.notes?.trim()) form.append('notes', body.notes.trim());
    if (body.issueDate?.trim()) form.append('issueDate', body.issueDate.trim());
    if (body.autoReceive === false) form.append('autoReceive', 'false');
    form.append('lines', JSON.stringify(body.lines ?? []));
    if (sourceFile) form.append('file', sourceFile, sourceFile.name);
    return this.http
      .post<
        ApiEnvelope<{ documentId: string; received: boolean; receiptId: string }>
      >(`${this.apiUrl}/delivery-notes/confirm`, form)
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  /**
   * Stáhne TT protokol příjemky (potvrzené řádky po editaci).
   * OCR originál: {@link downloadGoodsReceiptSource}.
   */
  downloadGoodsReceiptPdf(id: string): Observable<{ blob: Blob; filename: string }> {
    return this.http
      .get(`${this.apiUrl}/goods-receipt/${encodeURIComponent(id)}/pdf`, {
        responseType: 'blob',
        observe: 'response',
        headers: { Accept: 'application/pdf,application/octet-stream,*/*' },
      })
      .pipe(
        switchMap((res) => {
          const blob = res.body;
          const filename =
            parseContentDispositionFilename(res.headers.get('Content-Disposition')) ||
            `${id}.pdf`;
          return this.ensureDownloadBlob(blob, 'Export příjemky').pipe(
            map((safe) => ({
              blob: new Blob([safe], {
                type: (blob?.type || safe.type || 'application/octet-stream').trim(),
              }),
              filename,
            })),
          );
        }),
        catchError((e) => this.fail(e)),
      );
  }

  /** Nahraný OCR/DL originál příjemky (pokud je uložen). */
  downloadGoodsReceiptSource(id: string): Observable<{ blob: Blob; filename: string }> {
    return this.http
      .get(`${this.apiUrl}/goods-receipt/${encodeURIComponent(id)}/source`, {
        responseType: 'blob',
        observe: 'response',
        headers: { Accept: 'application/pdf,application/octet-stream,*/*' },
      })
      .pipe(
        switchMap((res) => {
          const blob = res.body;
          const filename =
            parseContentDispositionFilename(res.headers.get('Content-Disposition')) ||
            `${id}-zdroj`;
          return this.ensureDownloadBlob(blob, 'Zdrojový doklad příjemky').pipe(
            map((safe) => ({
              blob: new Blob([safe], {
                type: (blob?.type || safe.type || 'application/octet-stream').trim(),
              }),
              filename,
            })),
          );
        }),
        catchError((e) => this.fail(e)),
      );
  }

  listGoodsReceiptAttachments(
    receiptId: string,
  ): Observable<WarehouseGoodsReceiptAttachment[]> {
    return this.http
      .get<ApiEnvelope<{ list: WarehouseGoodsReceiptAttachment[] }>>(
        `${this.apiUrl}/goods-receipt/${encodeURIComponent(receiptId)}/attachments`,
      )
      .pipe(
        map((r) => this.unwrap(r).list ?? []),
        catchError((e) => this.fail(e)),
      );
  }

  uploadGoodsReceiptAttachment(
    receiptId: string,
    file: File,
  ): Observable<WarehouseGoodsReceiptAttachment> {
    const form = new FormData();
    form.append('file', file, file.name);
    return this.http
      .post<ApiEnvelope<WarehouseGoodsReceiptAttachment>>(
        `${this.apiUrl}/goods-receipt/${encodeURIComponent(receiptId)}/attachments`,
        form,
      )
      .pipe(map((r) => this.unwrap(r)), catchError((e) => this.fail(e)));
  }

  downloadGoodsReceiptAttachment(
    receiptId: string,
    attachmentId: string,
  ): Observable<{ blob: Blob; filename: string }> {
    return this.http
      .get(
        `${this.apiUrl}/goods-receipt/${encodeURIComponent(receiptId)}/attachments/${encodeURIComponent(attachmentId)}`,
        {
          responseType: 'blob',
          observe: 'response',
          headers: { Accept: 'application/pdf,application/octet-stream,*/*' },
        },
      )
      .pipe(
        switchMap((res) => {
          const blob = res.body;
          const filename =
            parseContentDispositionFilename(res.headers.get('Content-Disposition')) ||
            'priloha';
          return this.ensureDownloadBlob(blob, 'Příloha příjemky').pipe(
            map((safe) => ({
              blob: new Blob([safe], {
                type: (blob?.type || safe.type || 'application/octet-stream').trim(),
              }),
              filename,
            })),
          );
        }),
        catchError((e) => this.fail(e)),
      );
  }

  deleteGoodsReceiptAttachment(receiptId: string, attachmentId: string): Observable<void> {
    return this.http
      .delete<ApiEnvelope<void>>(
        `${this.apiUrl}/goods-receipt/${encodeURIComponent(receiptId)}/attachments/${encodeURIComponent(attachmentId)}`,
      )
      .pipe(
        map(() => undefined),
        catchError((e) => this.fail(e)),
      );
  }

  downloadGoodsIssuePdf(id: string): Observable<Blob> {
    return this.http
      .get(`${this.apiUrl}/goods-issue/${encodeURIComponent(id)}/pdf`, {
        responseType: 'blob',
        headers: { Accept: 'application/pdf' },
      })
      .pipe(
        switchMap((blob) => this.ensurePdfBlob(blob, 'PDF export výdejky')),
        catchError((e) => this.fail(e)),
      );
  }

  downloadTransferPdf(id: string): Observable<Blob> {
    return this.http
      .get(`${this.apiUrl}/transfer/${encodeURIComponent(id)}/pdf`, {
        responseType: 'blob',
        headers: { Accept: 'application/pdf' },
      })
      .pipe(
        switchMap((blob) => this.ensurePdfBlob(blob, 'PDF export převodky')),
        catchError((e) => this.fail(e)),
      );
  }

  downloadGoodsIssuesMaterialsPdf(params?: {
    warehouseId?: string;
    status?: string;
    createdBy?: string;
    polygonId?: string;
    dateFrom?: string;
    dateTo?: string;
    limit?: number;
    viewMode?: 'detail' | 'summary';
  }): Observable<Blob> {
    const httpParams: Record<string, string> = {};
    if (params?.warehouseId) httpParams['warehouseId'] = params.warehouseId;
    if (params?.status) httpParams['status'] = params.status;
    if (params?.createdBy) httpParams['createdBy'] = params.createdBy;
    if (params?.polygonId) httpParams['polygonId'] = params.polygonId;
    if (params?.dateFrom) httpParams['dateFrom'] = params.dateFrom;
    if (params?.dateTo) httpParams['dateTo'] = params.dateTo;
    if (params?.limit != null) httpParams['limit'] = String(params.limit);
    if (params?.viewMode) httpParams['viewMode'] = params.viewMode;
    return this.http
      .get(`${this.apiUrl}/goods-issue/materials-pdf`, {
        params: httpParams,
        responseType: 'blob',
        headers: { Accept: 'application/pdf' },
      })
      .pipe(
        switchMap((blob) => this.ensurePdfBlob(blob, 'PDF export výdeje')),
        catchError((e) => this.fail(e)),
      );
  }

  downloadGoodsReceiptsMaterialsPdf(params?: {
    warehouseId?: string;
    status?: string;
    dateFrom?: string;
    dateTo?: string;
    limit?: number;
    viewMode?: 'detail' | 'summary';
  }): Observable<Blob> {
    const httpParams: Record<string, string> = {};
    if (params?.warehouseId) httpParams['warehouseId'] = params.warehouseId;
    if (params?.status) httpParams['status'] = params.status;
    if (params?.dateFrom) httpParams['dateFrom'] = params.dateFrom;
    if (params?.dateTo) httpParams['dateTo'] = params.dateTo;
    if (params?.limit != null) httpParams['limit'] = String(params.limit);
    if (params?.viewMode) httpParams['viewMode'] = params.viewMode;
    return this.http
      .get(`${this.apiUrl}/goods-receipt/materials-pdf`, {
        params: httpParams,
        responseType: 'blob',
        headers: { Accept: 'application/pdf' },
      })
      .pipe(
        switchMap((blob) => this.ensurePdfBlob(blob, 'PDF export příjmu')),
        catchError((e) => this.fail(e)),
      );
  }

  /**
   * Reject JSON-as-binary (gateway mis-serialization) and empty payloads.
   * PDFs may arrive JSON-quoted; other OCR sources (image/txt) pass through.
   */
  private ensurePdfBlob(blob: Blob, label: string): Observable<Blob> {
    return this.ensureDownloadBlob(blob, label, { requirePdf: true });
  }

  private ensureDownloadBlob(
    blob: Blob | null,
    label: string,
    opts?: { requirePdf?: boolean },
  ): Observable<Blob> {
    if (!blob || blob.size === 0) {
      return throwError(() => new Error(`${label} vrátil prázdný soubor`));
    }
    return from(blob.arrayBuffer()).pipe(
      switchMap((buf) => {
        const bytes = new Uint8Array(buf);
        const unwrapped = unwrapJsonQuotedPdfBytes(bytes);
        if (unwrapped) {
          return of(new Blob([unwrapped], { type: 'application/pdf' }));
        }
        if (blob.type && blob.type.includes('json')) {
          let message = `${label} selhal`;
          try {
            const parsed = JSON.parse(new TextDecoder('utf-8').decode(bytes)) as {
              message?: string;
            };
            if (typeof parsed?.message === 'string' && parsed.message.trim()) {
              message = parsed.message.trim();
            }
          } catch {
            /* keep default */
          }
          return throwError(() => new Error(message));
        }
        if (opts?.requirePdf && !looksLikePdfMagic(bytes)) {
          return throwError(() => new Error(`${label} nevrátil platné PDF`));
        }
        return of(blob);
      }),
    );
  }

  private unwrap<T>(envelope: ApiEnvelope<T> | null | undefined): T {
    if (envelope?.success && envelope.data !== undefined) {
      return envelope.data;
    }
    throw new Error(envelope?.message || 'Neplatná odpověď skladu');
  }

  private fail(err: unknown): Observable<never> {
    if (err instanceof HttpErrorResponse) {
      const body = err.error as { message?: string } | string | null;
      const msg =
        (typeof body === 'object' && body?.message) ||
        (typeof body === 'string' ? body : null) ||
        err.message ||
        `HTTP ${err.status}`;
      return throwError(() => new Error(msg));
    }
    if (err instanceof Error) return throwError(() => err);
    return throwError(() => new Error(String(err)));
  }
}

/** `%PDF-` magic, or JSON-quoted PDF string from a misconfigured JSON proxy. */
function looksLikePdfMagic(bytes: Uint8Array): boolean {
  return (
    bytes.length >= 5 &&
    bytes[0] === 0x25 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x44 &&
    bytes[3] === 0x46 &&
    bytes[4] === 0x2d
  );
}

function unwrapJsonQuotedPdfBytes(bytes: Uint8Array): Uint8Array | null {
  if (looksLikePdfMagic(bytes)) {
    return bytes;
  }
  if (bytes[0] !== 0x22) return null;
  try {
    const parsed = JSON.parse(new TextDecoder('utf-8').decode(bytes)) as unknown;
    if (typeof parsed !== 'string' || !parsed.startsWith('%PDF-')) return null;
    const out = new Uint8Array(parsed.length);
    for (let i = 0; i < parsed.length; i++) {
      out[i] = parsed.charCodeAt(i) & 0xff;
    }
    return out;
  } catch {
    return null;
  }
}

function parseContentDispositionFilename(header: string | null): string | null {
  if (!header) return null;
  const utf = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header);
  if (utf?.[1]) {
    try {
      return decodeURIComponent(utf[1].trim().replace(/^"|"$/g, ''));
    } catch {
      /* fall through */
    }
  }
  const plain = /filename\s*=\s*("?)([^";]+)\1/i.exec(header);
  return plain?.[2]?.trim() || null;
}
